import { AuthProvider, COOKIE_TOKEN_NAME } from "context/auth"
import { ErrorProvider } from "context/error"
import type { GetServerSidePropsContext } from "next/types"
import React from "react"

import { Links } from "config/links"

import { URLS_FOR_CHECK_UTM, UTM_COOKIE_NAME, UTM_QUERY_NAMES, isUrlForUtm, saveUtmToCookie } from "./utm"

function getAuthToken(ctx: GetServerSidePropsContext): string | null {
  return ctx?.req?.cookies?.[COOKIE_TOKEN_NAME] ?? null
}

export type GetServerSidePropsWithAuthToken = Record<string, unknown> & {
  authToken?: string | null
}

type GetServerSidePropsWithAuthTokenArgs = {
  getServerSidePropsFunc?: (
    context: GetServerSidePropsContext,
    auth: {
      authToken?: string | null
    },
  ) =>
    | Promise<(Record<string, unknown> & { props?: GetServerSidePropsWithAuthToken }) | undefined>
    | (Record<string, unknown> & { props?: GetServerSidePropsWithAuthToken })
    | undefined
  authRequired?: boolean
}

function getServerSidePropsWithAuthToken(args?: GetServerSidePropsWithAuthTokenArgs) {
  return async (context: GetServerSidePropsContext) => {
    if (isUrlForUtm(context.resolvedUrl, URLS_FOR_CHECK_UTM)) {
      saveUtmToCookie(context, UTM_QUERY_NAMES, UTM_COOKIE_NAME, { path: "/", maxAge: 24 * 60 * 60 * 10 })
    }
    const authToken = getAuthToken(context)

    if (args?.authRequired) {
      if (!authToken) {
        return {
          redirect: {
            destination: Links.AUTH + `?referer=${encodeURIComponent(context.resolvedUrl)}`,
            permanent: false,
          },
        }
      }

      if (context.resolvedUrl === "/queue") {
        return {
          redirect: {
            destination: Links.GAME,
            permanent: false,
          },
        }
      }
    }
    if (args?.getServerSidePropsFunc) {
      const result = await args.getServerSidePropsFunc(context, { authToken })

      if (result) {
        result.props = { authToken, ...result.props }

        return result
      }
    }

    return {
      props: {
        authToken,
      },
    }
  }
}

function getLayoutWithAuth(
  layout?: (page: React.ReactElement, styles?: React.CSSProperties) => JSX.Element,
  styles?: React.CSSProperties,
) {
  // eslint-disable-next-line react/display-name
  return (page: React.ReactElement & { props: GetServerSidePropsWithAuthToken }) => {
    return (
      <ErrorProvider>
        <AuthProvider defaultToken={page.props.authToken}>{layout ? layout(page, styles) : page}</AuthProvider>
      </ErrorProvider>
    )
  }
}

export { getServerSidePropsWithAuthToken, getAuthToken, getLayoutWithAuth }
